import { Menu, type MenuVirtualTriggerProps } from "@overmap-ai/blocks"
import { type GeoImage, type Project, type Stored } from "@overmap-ai/common"
import { selectGeoImagesOfProject } from "@overmap-ai/core"
import { memo, useCallback, useMemo, useState } from "react"
import type { MarkerEvent } from "react-map-gl/mapbox"

import { GeoImageMarkerContextMenu } from "../../components/MapContextMenus/GeoImageMarkerContextMenu"
import { GeoImageViewerPopover } from "../../components/Popovers/GeoImageViewerPopover"
import { useAppSelector, useShallowEqaulSelector } from "../../store"
import { selectGeoImageDateRange } from "../../store/selectors"
import type { ModelAnnotatedEventHandler } from "../../typings"
import { useCurrentMapEvent } from "../hooks"
import { GeoImageMarker } from "./GeoImageMarker"

interface GeoImageMarkersProps {
	projectId: Project["uuid"]
}

export const GeoImageMarkers = memo<GeoImageMarkersProps>((props) => {
	const { projectId } = props

	const allGeoImages = useShallowEqaulSelector(selectGeoImagesOfProject(projectId))
	const geoImageDateRange = useAppSelector(selectGeoImageDateRange)
	const [activeGeoImage, setActiveGeoImage] = useState<Stored<GeoImage> | null>(null)

	// Filter images based on date range
	const geoImages = useMemo(() => {
		if (!geoImageDateRange || !geoImageDateRange.from) {
			return allGeoImages
		}

		return allGeoImages.filter((geoImage) => {
			// Skip images without a date
			if (!geoImage.original_date) return false

			const imageDate = new Date(geoImage.original_date)
			const startDate = geoImageDateRange.from as Date // The null check is already done above
			const endDate = geoImageDateRange.to ? new Date(geoImageDateRange.to) : new Date()

			// Include only images within the date range
			return imageDate >= startDate && imageDate <= endDate
		})
	}, [allGeoImages, geoImageDateRange])

	const [clickVirtualTrigger, setClickVirtualTrigger] = useState<MenuVirtualTriggerProps["virtualElement"]>(null)
	const [contextMenuVirtualTrigger, setContextMenuVirtualTrigger] =
		useState<MenuVirtualTriggerProps["virtualElement"]>(null)

	const handleGeoImageMarkerContextMenuClose = useCallback(() => {
		setActiveGeoImage(null)
		setContextMenuVirtualTrigger(null)
	}, [])

	const closeGeoImageMarkerPopover = useCallback(() => {
		setActiveGeoImage(null)
		setClickVirtualTrigger(null)
	}, [])

	const handleGeoMarkerClick = useCallback<ModelAnnotatedEventHandler<MarkerEvent<MouseEvent>, Stored<GeoImage>>>(
		({ event, model: geoImage }) => {
			const { target } = event

			handleGeoImageMarkerContextMenuClose()
			setActiveGeoImage(geoImage)
			setClickVirtualTrigger({
				getBoundingClientRect() {
					return target.getElement().getBoundingClientRect()
				},
			})
		},
		[handleGeoImageMarkerContextMenuClose],
	)

	const handleGeoImageMarkerContextMenu = useCallback<
		ModelAnnotatedEventHandler<MarkerEvent<MouseEvent>, Stored<GeoImage>>
	>(
		({ event, model: geoImage }) => {
			const { target } = event

			closeGeoImageMarkerPopover()
			setActiveGeoImage(geoImage)
			setContextMenuVirtualTrigger({
				getBoundingClientRect() {
					return target.getElement().getBoundingClientRect()
				},
			})
		},
		[closeGeoImageMarkerPopover],
	)

	useCurrentMapEvent("zoomstart", handleGeoImageMarkerContextMenuClose)

	return (
		<>
			{geoImages.map((geoImage) => (
				<GeoImageMarker
					geoImage={geoImage}
					key={geoImage.uuid}
					onClick={handleGeoMarkerClick}
					onContextMenu={handleGeoImageMarkerContextMenu}
				/>
			))}
			{activeGeoImage && (
				<>
					<GeoImageViewerPopover key={activeGeoImage.uuid} geoImageId={activeGeoImage.uuid} size="sm">
						<Menu.VirtualTrigger virtualElement={clickVirtualTrigger} />
					</GeoImageViewerPopover>
					<GeoImageMarkerContextMenu geoImage={activeGeoImage}>
						<Menu.VirtualTrigger virtualElement={contextMenuVirtualTrigger} />
					</GeoImageMarkerContextMenu>
				</>
			)}
		</>
	)
})
GeoImageMarkers.displayName = "GeoImagesLayer"
